import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  TextField,
  Button,
  Stack,
  MenuItem,
} from '@mui/material';
import { StringType, Material } from '../types';
import { useScrollIntoView } from '../hooks/useScrollIntoView';

interface Props {
  onSave: (data: Omit<StringType, 'id'>) => void;
  onCancel: () => void;
  initialData?: Omit<StringType, 'id'>;
  readOnly?: boolean;
  setReadOnly?: (editMode: boolean) => void;
}

export default function CreateStringTypeForm({ onSave, onCancel, initialData, readOnly, setReadOnly }: Props) {
  const scrollIntoView = useScrollIntoView();
  const [brand, setBrand] = useState('');
  const [name, setName] = useState('');
  const [gauge, setGauge] = useState('');
  const [color, setColor] = useState('');
  const [material, setMaterial] = useState<Material>(Material.POLYESTER);
  const [stiffness, setStiffness] = useState<number | ''>('');
  const [spinPotential, setSpinPotential] = useState<number | ''>('');

  useEffect(() => {
    if (initialData) {
      setBrand(initialData.brand);
      setName(initialData.name);
      setGauge(initialData.gauge ?? '');
      setColor(initialData.color ?? '');
      setMaterial(initialData.material);
      setStiffness(initialData.stiffness ?? '');
      setSpinPotential(initialData.spinPotential ?? '');
    }
  }, [initialData]);

  const handleTextFieldFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    scrollIntoView(e.target);
  };
  
  const handleSubmit = () => {
    if (!brand || !name) {
      alert('Brand and Name are required');
      return;
    }

    onSave({
      brand,
      name,
      gauge: gauge || undefined,
      color: color || undefined,
      material,
      stiffness: stiffness === '' ? undefined : stiffness,
      spinPotential: spinPotential === '' ? undefined : spinPotential,
    });
  };

  const handleEdit = () => {
    if (setReadOnly) {
      setReadOnly(false);
    };
  }

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Brand"
            value={brand}
            onChange={(e) => setBrand(e.target.value)}
            disabled={readOnly}
            onFocus={handleTextFieldFocus}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={readOnly}
            onFocus={handleTextFieldFocus}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Gauge"
            value={gauge}
            onChange={(e) => setGauge(e.target.value)}
            placeholder="e.g. 17"
            disabled={readOnly}
            onFocus={handleTextFieldFocus}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            disabled={readOnly}
            onFocus={handleTextFieldFocus}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            select
            fullWidth
            label="Material"
            value={material}
            onChange={(e) => setMaterial(e.target.value as Material)}
            disabled={readOnly}
            onFocus={handleTextFieldFocus}
          >
            <MenuItem value="SYNTHETIC">Synthetic</MenuItem>
            <MenuItem value="GUT">Gut</MenuItem>
            <MenuItem value="MULTIFILAMENT">Multifilament</MenuItem>
            <MenuItem value="POLYESTER">Polyester</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type="number"
            label="Stiffness"
            value={stiffness}
            onChange={(e) =>
              setStiffness(e.target.value ? parseFloat(e.target.value) : '')
            }
            disabled={readOnly}
            onFocus={handleTextFieldFocus}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type="number"
            label="Spin Potential"
            value={spinPotential}
            onChange={(e) =>
              setSpinPotential(e.target.value ? parseFloat(e.target.value) : '')
            }
            disabled={readOnly}
            onFocus={handleTextFieldFocus}
          />
        </Grid>
      </Grid>

      { !readOnly && (
        <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 2 }}>
          <Button variant="text" onClick={onCancel}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}>Save</Button>
        </Stack>
      )}

      { readOnly && (
        <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 2 }}>
          <Button variant="text" onClick={onCancel}>Cancel</Button>
          <Button variant="contained" onClick={handleEdit}>Edit</Button>
        </Stack>
      )}
    </Box>
  );
}
